import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, ExternalLink, FolderLock, Layers, Loader, RefreshCw, ScrollText, Server } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LogbookEventList } from './LogbookEventList';
import { LogbookBreakdown } from './LogbookBreakdown';
import { LogbookActivityChart } from './LogbookActivityChart';
import { LogbookCompleteness } from './LogbookCompleteness';
import { AddToCaseModal } from './AddToCaseModal';
import { LogbookViewBar } from './LogbookViewBar';
import { EmptyState } from '../ui/EmptyState';
import { SectionHeader } from '../ui/SectionHeader';
import { StatTile } from '../ui/StatTile';
import { useAuth } from '../../contexts/AuthContext';
import { useLogbookServers } from '../../hooks/useLogbookServers';
import { logbookApiService, LogbookApiError } from '../../services/logbookApi';
import { canProtectLogbook, canViewLogbook } from '../../utils/logbookPermissions';
import { LOGBOOK_PRESETS, LOGBOOK_WINDOWS, formatCount, isEvidenceGap } from '../../utils/logbook';
import { DEFAULT_LOGBOOK_FILTERS, filtersToParams, toAggregationQuery, toSearchQuery, type LogbookFilterState } from '../../utils/logbookFilters';
import { DEFAULT_LOGBOOK_VIEW, groupEvents, partitionEvents, type LogbookViewOptions } from '../../utils/logbookView';
import { cn } from '../../utils/cn';
import type { LogbookAggregationResult, LogbookEvent, LogbookSearchResult } from '../../types/logbook';

interface LogbookPlayerTimelineProps {
  /** Minecraft UUID. The timeline endpoint needs an actor, and a UUID is the stable one. */
  playerUuid: string;
  playerName?: string;
  className?: string;
}

/**
 * One player's Logbook history, embedded wherever that player already is.
 *
 * This is the shape most investigations take: a report names a person, and the next question is what
 * that person did. Reaching it from their profile rather than from a separate search keeps the answer
 * next to their notes, warnings, bans and relationships, where it is actually read.
 *
 * Counts come from aggregations over the whole window, so the tiles and the breakdowns describe every
 * matching record rather than the page on screen. The page itself is a snapshot, which the coverage
 * line explains rather than hides.
 */
export const LogbookPlayerTimeline = ({ playerUuid, playerName, className }: LogbookPlayerTimelineProps) => {
  const { identity } = useAuth();
  const allowed = canViewLogbook(identity);
  const canProtect = canProtectLogbook(identity);
  const { byId: serversById } = useLogbookServers();

  const [windowId, setWindowId] = useState('7d');
  const [actions, setActions] = useState<string[]>([]);
  const [page, setPage] = useState<LogbookSearchResult | null>(null);
  const [events, setEvents] = useState<LogbookEvent[]>([]);
  const [byAction, setByAction] = useState<LogbookAggregationResult | null>(null);
  const [byServer, setByServer] = useState<LogbookAggregationResult | null>(null);
  const [overTime, setOverTime] = useState<LogbookAggregationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [paging, setPaging] = useState(false);
  const [error, setError] = useState<LogbookApiError | Error | null>(null);
  const [held, setHeld] = useState<Set<string>>(new Set());
  const [attaching, setAttaching] = useState<LogbookEvent[] | null>(null);
  const [caseNote, setCaseNote] = useState<string | null>(null);
  const [view, setView] = useState<LogbookViewOptions>(DEFAULT_LOGBOOK_VIEW);
  const [nonce, setNonce] = useState(0);

  const filters = useMemo<LogbookFilterState>(
    () => ({ ...DEFAULT_LOGBOOK_FILTERS, windowId, actions, actorId: playerUuid, limit: 50 }),
    [windowId, actions, playerUuid],
  );

  useEffect(() => {
    if (!allowed || !playerUuid) return;
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      const now = Date.now();
      try {
        const [timeline, actionCounts, serverCounts, timeCounts] = await Promise.all([
          logbookApiService.timeline({ ...toSearchQuery(filters, now), actor_id: playerUuid }, controller.signal),
          logbookApiService.aggregate(toAggregationQuery(filters, 'action', now), controller.signal).catch(() => null),
          logbookApiService.aggregate(toAggregationQuery(filters, 'server_id', now), controller.signal).catch(() => null),
          logbookApiService.aggregate(toAggregationQuery(filters, 'time', now), controller.signal).catch(() => null),
        ]);
        if (cancelled) return;
        setPage(timeline);
        setEvents(timeline.events);
        setByAction(actionCounts);
        setByServer(serverCounts);
        setOverTime(timeCounts);
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setPage(null);
        setEvents([]);
        setError(caught instanceof Error ? caught : new Error('The timeline could not be loaded.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => { cancelled = true; controller.abort(); };
  }, [allowed, playerUuid, filters, nonce]);

  const loadMore = useCallback(async () => {
    if (!page?.cursor || paging) return;
    setPaging(true);
    try {
      const next = await logbookApiService.timeline({ ...toSearchQuery(filters), actor_id: playerUuid, cursor: page.cursor });
      setPage(next);
      setEvents((current) => [...current, ...next.events]);
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error('The next page could not be loaded.'));
    } finally {
      setPaging(false);
    }
  }, [page, paging, filters, playerUuid]);

  const totals = useMemo(() => {
    const total = (byAction?.groups ?? []).reduce((sum, group) => sum + group.event_count, 0);
    const gaps = events.filter(isEvidenceGap).length;
    return { total, gaps, servers: byServer?.groups.length ?? 0, actions: byAction?.groups.length ?? 0 };
  }, [byAction, byServer, events]);

  const partition = useMemo(() => partitionEvents(events, view, held), [events, view, held]);
  const runs = useMemo(() => groupEvents(partition.visible, view.groupWindowMs), [partition.visible, view.groupWindowMs]);

  const oldestReceipt = useMemo(() => {
    const receipts = (page?.scope ?? [])
      .map((serverId) => serversById.get(serverId)?.lastReceivedAt)
      .filter((value): value is number => typeof value === 'number');
    return receipts.length > 0 ? Math.min(...receipts) : null;
  }, [page, serversById]);

  const investigateHref = useMemo(() => {
    const params = filtersToParams(filters);
    params.set('actorId', playerUuid);
    return `/logbook?${params.toString()}`;
  }, [filters, playerUuid]);

  if (!allowed) {
    return (
      <EmptyState
        icon={ScrollText}
        title="Logbook evidence is restricted"
        description="Server-side evidence for this player needs the argus.logbook.read permission."
      />
    );
  }

  if (error && events.length === 0 && !loading) {
    const notConfigured = error instanceof LogbookApiError && error.isNotConfigured;
    return (
      <EmptyState
        icon={ScrollText}
        tone={notConfigured ? 'neutral' : 'danger'}
        title={notConfigured ? 'Logbook is not connected yet' : 'Evidence could not be loaded'}
        description={error.message}
        action={!notConfigured && (
          <button type="button" className="glass-button" onClick={() => setNonce((value) => value + 1)}>
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        )}
      />
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Window and action presets: the same vocabulary as the full investigation page */}
      <div className="glass-card p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-2">
        <select
          value={windowId}
          onChange={(changed) => setWindowId(changed.target.value)}
          className="glass-select md:w-48"
          aria-label="Time window"
        >
          {LOGBOOK_WINDOWS.map((window) => <option key={window.id} value={window.id}>{window.label}</option>)}
        </select>

        <div className="flex-1 flex flex-wrap items-center gap-1.5">
          {LOGBOOK_PRESETS.filter((preset) => preset.id !== 'integrity').map((preset) => {
            const Icon = preset.icon;
            const active = preset.actions.length === actions.length && preset.actions.every((action) => actions.includes(action));
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => setActions(active ? [] : preset.actions)}
                className={cn('chip gap-1.5', active && 'border-accent-400/50 bg-accent-500/15')}
                title={preset.description}
              >
                <Icon className="w-3.5 h-3.5" />
                {preset.label}
              </button>
            );
          })}
        </div>

        <Link to={investigateHref} className="glass-button-sm shrink-0">
          <ExternalLink className="w-4 h-4" />
          Open in Logbook
        </Link>
      </div>

      {caseNote && (
        <div className="alert alert-info">
          <FolderLock className="w-4 h-4 shrink-0" />
          <span className="flex-1">{caseNote}</span>
          <Link to="/logbook/cases" className="link text-xs shrink-0">Open cases</Link>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Captured events" value={formatCount(totals.total)} icon={Activity} tone="accent" loading={loading} />
        <StatTile label="Distinct actions" value={formatCount(totals.actions)} icon={Layers} tone="info" loading={loading} />
        <StatTile label="Servers" value={formatCount(totals.servers)} icon={Server} tone="purple" loading={loading} />
        <StatTile
          label="Capture gaps"
          value={formatCount(totals.gaps)}
          icon={ScrollText}
          tone={totals.gaps > 0 ? 'warning' : 'neutral'}
          loading={loading}
          hint={totals.gaps > 0 ? 'Evidence is incomplete here' : undefined}
        />
      </div>

      <LogbookActivityChart aggregation={overTime} loading={loading} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <LogbookBreakdown title="By action" icon={Layers} aggregation={byAction} loading={loading} />
        <LogbookBreakdown title="By server" icon={Server} aggregation={byServer} loading={loading} serversById={serversById} />
      </div>

      <div className="glass-card overflow-hidden">
        <div className="p-4 md:p-5 pb-3">
          <SectionHeader
            title={playerName ? `${playerName}'s evidence` : 'Evidence'}
            icon={ScrollText}
            count={partition.visible.length}
          />
          <LogbookCompleteness
            completeness={page?.completeness}
            scope={page?.scope}
            snapshot={page?.snapshot}
            gapCount={totals.gaps}
            oldestReceiptAt={oldestReceipt}
            className="mt-3"
          />
          <LogbookViewBar value={view} onChange={setView} partition={partition} runCount={runs.length} className="mt-3" />
        </div>

        {runs.length === 0 && !loading ? (
          <div className="px-4 pb-6">
            <EmptyState
              bare
              compact
              icon={ScrollText}
              title={partition.hiddenTotal > 0 ? 'Everything on this page is hidden' : 'No captured evidence in this window'}
              description={partition.hiddenTotal > 0
                ? `${formatCount(partition.hiddenTotal)} records are hidden by the view settings above.`
                : 'Widen the window or clear the action filter.'}
            />
          </div>
        ) : (
          <LogbookEventList
            runs={runs}
            serversById={serversById}
            loading={loading}
            hideActor
            canProtect={canProtect}
            heldEventIds={held}
            onAddToCase={setAttaching}
          />
        )}

        {page?.cursor && (
          <div className="p-4 border-t border-glass-200/10 flex items-center justify-between gap-3">
            <span className="text-xs text-glass-500">
              {formatCount(partition.visible.length)} shown of {formatCount(events.length)} loaded.
            </span>
            <button type="button" className="glass-button-sm" onClick={loadMore} disabled={paging}>
              {paging ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Load more
            </button>
          </div>
        )}
      </div>

      {attaching && (
        <AddToCaseModal
          events={attaching}
          onClose={() => setAttaching(null)}
          onAttached={(result, target) => {
            // The lock on a row comes from what this session attached; nothing is re-fetched to
            // discover it, because a page of results is not worth a lookup per row.
            setHeld((current) => {
              const next = new Set(current);
              for (const event of attaching) next.add(event.event_id);
              return next;
            });
            setCaseNote(
              `${result.attached} ${result.attached === 1 ? 'record' : 'records'} copied into ${target.reference}`
              + (result.duplicates > 0 ? `, ${result.duplicates} already there.` : '.'),
            );
          }}
        />
      )}

    </div>
  );
};
